<div class="page">
    <div class="page-head">
        <div>
            <h2>{{ pageTitle() || 'Partner KYC' }}</h2>
            <p>Complete your KYC to unlock the full application. An admin reviews every submission.</p>
        </div>
        @if (isApproved) {
        <p-button label="Go to App" icon="pi pi-arrow-right" (onClick)="goToApp()" />
        }
    </div>

    <!-- ===================== STATUS BANNER ===================== -->
    @if (status() === 'NotSubmitted') {
    <div class="banner info">
        <i class="pi pi-info-circle"></i>
        <div>
            <strong>KYC not submitted</strong>
            <span>Fill in the form below and submit to begin verification.</span>
        </div>
    </div>
    } @else if (isPending) {
    <div class="banner warn">
        <i class="pi pi-clock"></i>
        <div>
            <strong>KYC under review</strong>
            <span>Your details were submitted and are locked while an admin reviews them.</span>
        </div>
    </div>
    } @else if (isApproved) {
    <div class="banner ok">
        <i class="pi pi-check-circle"></i>
        <div>
            <strong>KYC approved</strong>
            <span>You have full access to the app.</span>
        </div>
    </div>
    } @else if (isRejected) {
    <div class="banner danger">
        <i class="pi pi-times-circle"></i>
        <div>
            <strong>KYC rejected</strong>
            <span>{{ rejectionReason() || 'Please correct your details and resubmit.' }}</span>
        </div>
    </div>
    }

    @if (loading()) {
    <div class="card"><div class="state"><i class="pi pi-spin pi-spinner"></i> Loading your KYC...</div></div>
    } @else {
    <form [formGroup]="form" class="card kyc-form">
        @if (partnerName() || partnerCode()) {
        <p class="hint-muted">
            Partner: <strong>{{ partnerName() }}</strong>
            @if (partnerCode()) { <span class="mono">{{ partnerCode() }}</span> }
        </p>
        }

        <!-- Company -->
        <h3 class="section-title"><i class="pi pi-building"></i> Company Details</h3>
        <div class="grid-2">
            <div class="field span-2">
                <label>Company Name <span class="req">*</span></label>
                <input pInputText type="text" formControlName="fullName" placeholder="Registered company name" maxlength="150">
                @if (form.get('fullName')?.touched && form.get('fullName')?.invalid) {
                <small class="err">Company name is required (max 150 characters)</small>
                }
            </div>
        </div>

        <!-- Documents -->
        <h3 class="section-title"><i class="pi pi-file"></i> KYC Documents</h3>
        <div class="grid-3">
            <div class="field">
                <label>Company PAN Card <span class="req">*</span></label>
                <input pInputText type="text" formControlName="panNumber" placeholder="ABCDE1234F"
                    maxlength="10" class="upper">
                @if (form.get('panNumber')?.touched && form.get('panNumber')?.invalid) {
                @if (form.get('panNumber')?.errors?.['required']) {
                <small class="err">Company PAN is required</small>
                } @else {
                <small class="err">Invalid PAN (format ABCDE1234F)</small>
                }
                }
            </div>
            <div class="field">
                <label>Company Registration No. <span class="req">*</span></label>
                <input pInputText type="text" formControlName="aadhaarNumber" maxlength="30"
                    placeholder="e.g. U74999MH2020PTC123456" class="upper">
                @if (form.get('aadhaarNumber')?.touched && form.get('aadhaarNumber')?.invalid) {
                @if (form.get('aadhaarNumber')?.errors?.['required']) {
                <small class="err">Company registration number is required</small>
                } @else {
                <small class="err">Enter a valid registration number</small>
                }
                }
            </div>
            <div class="field">
                <label>GST Number</label>
                <input pInputText type="text" formControlName="gstNumber" placeholder="22ABCDE1234F1Z5" maxlength="15"
                    class="upper">
                @if (form.get('gstNumber')?.touched && form.get('gstNumber')?.invalid) {
                <small class="err">Invalid GSTIN (15 characters)</small>
                }
            </div>
        </div>

        <!-- Contact -->
        <h3 class="section-title"><i class="pi pi-phone"></i> Contact</h3>
        <div class="grid-2">
            <div class="field">
                <label>Company Email</label>
                <input pInputText type="email" formControlName="email" placeholder="company@email.com" maxlength="150">
                @if (form.get('email')?.touched && form.get('email')?.invalid) {
                <small class="err">Enter a valid email (max 150 characters)</small>
                }
            </div>
            <div class="field">
                <label>Mobile Number <span class="req">*</span></label>
                <input pInputText type="text" formControlName="mobileNumber" appDigitsOnly="10"
                    inputmode="numeric" maxlength="10" placeholder="9876543210">
                @if (form.get('mobileNumber')?.touched && form.get('mobileNumber')?.invalid) {
                @if (form.get('mobileNumber')?.errors?.['required']) {
                <small class="err">Mobile number is required</small>
                } @else {
                <small class="err">Enter a valid 10-digit mobile number</small>
                }
                }
            </div>
        </div>

        <!-- Address -->
        <h3 class="section-title"><i class="pi pi-map-marker"></i> Address</h3>
        <div class="grid-2">
            <div class="field span-2">
                <label>Address Line 1 <span class="req">*</span></label>
                <input pInputText type="text" formControlName="addressLine1" placeholder="Street, area" maxlength="250">
                @if (form.get('addressLine1')?.touched && form.get('addressLine1')?.invalid) {
                <small class="err">Address is required (max 250 characters)</small>
                }
            </div>
            <div class="field span-2">
                <label>Address Line 2</label>
                <input pInputText type="text" formControlName="addressLine2" placeholder="Landmark (optional)" maxlength="250">
            </div>
            <div class="field">
                <label>City <span class="req">*</span></label>
                <input pInputText type="text" formControlName="city" placeholder="Mumbai" maxlength="100">
                @if (form.get('city')?.touched && form.get('city')?.invalid) {
                <small class="err">City is required</small>
                }
            </div>
            <div class="field">
                <label>State <span class="req">*</span></label>
                <input pInputText type="text" formControlName="state" placeholder="Maharashtra" maxlength="100">
                @if (form.get('state')?.touched && form.get('state')?.invalid) {
                <small class="err">State is required</small>
                }
            </div>
            <div class="field">
                <label>Pincode <span class="req">*</span></label>
                <input pInputText type="text" formControlName="pincode" appDigitsOnly="6" inputmode="numeric"
                    placeholder="400001" maxlength="6">
                @if (form.get('pincode')?.touched && form.get('pincode')?.invalid) {
                @if (form.get('pincode')?.errors?.['required']) {
                <small class="err">Pincode is required</small>
                } @else {
                <small class="err">Enter a valid 6-digit pincode</small>
                }
                }
            </div>
        </div>

        <!-- Bank -->
        <h3 class="section-title"><i class="pi pi-wallet"></i> Bank</h3>
        <div class="grid-3">
            <div class="field">
                <label>Bank Name</label>
                <input pInputText type="text" formControlName="bankName" placeholder="HDFC Bank" maxlength="150">
            </div>
            <div class="field">
                <label>Account Holder Name</label>
                <input pInputText type="text" formControlName="accountHolderName" placeholder="As per bank" maxlength="150">
            </div>
            <div class="field">
                <label>Account Number</label>
                <input pInputText type="text" formControlName="accountNumber" appDigitsOnly="18" inputmode="numeric"
                    placeholder="000111222333" maxlength="18">
                @if (form.get('accountNumber')?.touched && form.get('accountNumber')?.invalid) {
                <small class="err">Enter 6–18 digits</small>
                }
            </div>
            <div class="field">
                <label>IFSC Code</label>
                <input pInputText type="text" formControlName="ifscCode" placeholder="HDFC0001234" maxlength="11"
                    class="upper">
                @if (form.get('ifscCode')?.touched && form.get('ifscCode')?.invalid) {
                <small class="err">Invalid IFSC (e.g. HDFC0001234)</small>
                }
            </div>
            <div class="field">
                <label>Branch Name</label>
                <input pInputText type="text" formControlName="branchName" placeholder="Andheri West" maxlength="150">
            </div>
        </div>

        <!-- Uploads -->
        <h3 class="section-title"><i class="pi pi-upload"></i> Uploads <span class="muted">(jpg, png, pdf, docx — max 10 MB)</span></h3>
        <div class="grid-2">
            <div class="field">
                <label>Company PAN Document</label>
                <app-doc-upload formControlName="panFilePath" module="PartnerKyc" />
            </div>
            <div class="field">
                <label>Registration Document</label>
                <app-doc-upload formControlName="aadhaarFilePath" module="PartnerKyc" />
            </div>
            <div class="field">
                <label>GST Document</label>
                <app-doc-upload formControlName="gstFilePath" module="PartnerKyc" />
            </div>
            <div class="field">
                <label>Cancelled Cheque</label>
                <app-doc-upload formControlName="cancelledChequePath" module="PartnerKyc" />
            </div>
        </div>

        @if (!isApproved && !isPending) {
        <div class="form-actions">
            <p-button type="button" (onClick)="submit()" [disabled]="saving()"
                [label]="saving() ? 'Submitting...' : (isRejected ? 'Resubmit KYC' : 'Submit KYC')"
                [icon]="saving() ? 'pi pi-spin pi-spinner' : 'pi pi-check'" />
        </div>
        }
    </form>
    }
</div>
